import React, { useEffect, useMemo, useState } from "react";
import useCatalogue from "../../../services/Order/UseCatalogue";
import useLabel from "../../../services/Pos/useLabel";
import { toast } from "react-toastify";
import { ChevronDown } from "lucide-react";

const LabelPrintingSystem = () => {
  const { catalogue, loading, fetchCatalogue } = useCatalogue();
  const { pushItemBarcode } = useLabel();

const [formData, setFormData] = useState({
  category: "",
  item: "",
  mfgDate: "",
  expDate: "",
  mrp: "",
  labelQuantity: 1,
  weight: "",
  description: "",
  designType: "",
  isCustomDescription: false,
});

const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
const [showItemDropdown, setShowItemDropdown] = useState(false);

const [categorySearch, setCategorySearch] = useState("");
const [itemSearch, setItemSearch] = useState("");

  useEffect(() => {
    fetchCatalogue();
  }, []);

  const categories = useMemo(() => {
    return catalogue || [];
  }, [catalogue]);

  const selectedCategory = useMemo(() => {
    return categories.find(
      (cat) => String(cat.cat_id) === String(formData.category),
    );
  }, [categories, formData.category]);

  const items = useMemo(() => {
    return selectedCategory?.menu_items || [];
  }, [selectedCategory]);

  const selectedItem = useMemo(() => {
  return items.find((item) => String(item.item_id) === String(formData.item));
}, [items, formData.item]);

  const formatDateForApi = (dateValue) => {
    if (!dateValue) return "";

    const [year, month, day] = dateValue.split("-");
    return `${day}-${month}-${year}`;
  };

  const generateUniqueId = () => {
    return `${Date.now()}${Math.floor(Math.random() * 1000)}`;
  };

  const getDefaultDescription = (item) => {
  if (!item) return "";

  return (
    item.description ||
    item.item_description ||
    item.short_description ||
    item.item_name ||
    ""
  );
};

  const designType = formData.designType;

const barcodeImage =
  designType === "1"
    ? "https://anlook.com/images/barcode/type_1.png"
    : designType === "2"
      ? "https://anlook.com/images/barcode/type_2.png"
      : designType === "3"
        ? "https://anlook.com/images/barcode/type_1.png"
        : "";

 const previewLabelSize =
  designType === "1"
    ? "50 x 50"
    : designType === "2"
      ? "80 x 40"
      : designType === "3"
        ? "50 x 50"
        : "Select design type";

  const payloadWeight = `${formData.weight || "-"}|${
    formData.labelQuantity || "-"
  }|${formData.mrp || "-"}|${formData.description || "-"}|${designType || "-"}`;

  const inputClass =
    "w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-400";

  const labelClass = "mb-1.5 block text-sm font-medium text-slate-700";

  const handleChange = (e) => {
  const { name, value } = e.target;

  setFormData((prev) => ({
    ...prev,
    [name]: value,

    ...(name === "description"
      ? { isCustomDescription: true }
      : {}),

    ...(name === "category"
      ? {
          item: "",
          mrp: "",
          description: "",
          isCustomDescription: false,
        }
      : {}),
  }));
};

 const handleItemChange = (e) => {
  const selectedItemId = e.target.value;

  const item = items.find(
    (item) => String(item.item_id) === String(selectedItemId),
  );

  const firstPrice = item?.prices?.[0];

  const defaultDescription = getDefaultDescription(item);

  setFormData((prev) => ({
    ...prev,
    item: selectedItemId,
    mrp:
      firstPrice?.price ||
      firstPrice?.mrp ||
      firstPrice?.takeaway_price ||
      firstPrice?.dining_price ||
      firstPrice?.home_delivery_price ||
      "",

    description: prev.isCustomDescription
      ? prev.description
      : defaultDescription,
  }));
};

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast("Please select category");
      return;
    }

    if (!formData.item) {
      toast("Please select item");
      return;
    }

    if (!formData.mfgDate) {
      toast("Please select MFG date");
      return;
    }

    if (!formData.expDate) {
      toast("Please select EXP date");
      return;
    }

    if (!formData.weight) {
      toast("Please enter weight");
      return;
    }

    if (!formData.mrp) {
      toast("Please enter MRP");
      return;
    }

    if (!formData.description.trim()) {
  toast.error("Please enter description");
  return;
}

if (!formData.designType) {
  toast.error("Please select design type");
  return;
}

    const finalDesignType = formData.designType;

    const request = {
      itemid: formData.item,
      mfg_date: formatDateForApi(formData.mfgDate),
      expiry_date: formatDateForApi(formData.expDate),
      weight: `${formData.weight}|${formData.labelQuantity}|${formData.mrp}|${formData.description}|${finalDesignType}`,
      uniqId: generateUniqueId(),
    };

    console.log("Barcode Request:", request);

    await pushItemBarcode([request]);
  };

const filteredCategories = categories.filter((cat) =>
  (cat.category_name || "")
    .toLowerCase()
    .includes(categorySearch.toLowerCase())
);

const filteredItems = items.filter((item) =>
  (item.item_name || "")
    .toLowerCase()
    .includes(itemSearch.toLowerCase())
);

  return (
    <>
  
    <div className="h-full flex flex-col overflow-hidden px-4 py-6 md:px-8">
      <div className="sticky top-0 z-30 pb-6">
  <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-1 text-sm font-medium text-emerald-600">
            Barcode & Label
          </p>
          <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
            Label Printing System
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Create item barcode labels with MFG, expiry, weight and live
            preview.
          </p>
        </div>

        <div className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
          Design Type: {designType || "-"} • {previewLabelSize}
        </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
  <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_420px]">
        {/* LEFT FORM */}
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-5">
            <h2 className="text-lg font-bold text-slate-900">Create Label</h2>
            <p className="mt-1 text-sm text-slate-500">
              Select catalogue item and generate barcode payload.
            </p>
          </div>

          <form onSubmit={handleCreate} className="p-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelClass}>Category</label>

                <div className="relative">
  <div
    onClick={() => {
      if (!loading) {
        setShowCategoryDropdown((prev) => !prev);
        setShowItemDropdown(false);
      }
    }}
    className={`${inputClass} flex items-center justify-between cursor-pointer`}
  >
    <span>
      {selectedCategory?.category_name ||
        (loading ? "Loading..." : "Select Category")}
    </span>

    <ChevronDown
  size={18}
  className={`text-gray-500 transition-transform duration-200 ${
    showCategoryDropdown ? "rotate-180" : ""
  }`}
/>
  </div>

  {showCategoryDropdown && (
    <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow-lg">
      <input
        autoFocus
        type="text"
        placeholder="Search category..."
        value={categorySearch}
        onChange={(e) => setCategorySearch(e.target.value)}
        className="w-full px-3 py-2 border-b outline-none"
      />

      <div className="max-h-52 overflow-y-auto">
        {filteredCategories.map((category) => (
          <button
            type="button"
            key={category.cat_id}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                category: category.cat_id,
                item: "",
                mrp: "",
                description: "",
                isCustomDescription: false,
              }));

              setCategorySearch("");
              setShowCategoryDropdown(false);
            }}
            className="w-full text-left px-3 py-2 hover:bg-emerald-50"
          >
            {category.category_name}
          </button>
        ))}
      </div>
    </div>
  )}
</div>
              </div>

              <div>
                <label className={labelClass}>Item</label>

                <div className="relative">
  <div
    onClick={() => {
      if (formData.category) {
        setShowItemDropdown((prev) => !prev);
        setShowCategoryDropdown(false);
      }
    }}
    className={`${inputClass} flex items-center justify-between cursor-pointer ${
      !formData.category ? "opacity-60 cursor-not-allowed" : ""
    }`}
  >
    <span>{selectedItem?.item_name || "Select Item"}</span>

    <ChevronDown
  size={18}
  className={`text-gray-500 transition-transform duration-200 ${
    showItemDropdown ? "rotate-180" : ""
  }`}
/>
  </div>

  {showItemDropdown && (
    <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow-lg">
      <input
        autoFocus
        type="text"
        placeholder="Search item..."
        value={itemSearch}
        onChange={(e) => setItemSearch(e.target.value)}
        className="w-full px-3 py-2 border-b outline-none"
      />

      <div className="max-h-52 overflow-y-auto">
        {filteredItems.map((item) => (
          <button
            type="button"
            key={item.item_id}
            onClick={() => {
              const firstPrice = item?.prices?.[0];

              setFormData((prev) => ({
                ...prev,
                item: item.item_id,
                mrp:
                  firstPrice?.price ||
                  firstPrice?.mrp ||
                  firstPrice?.takeaway_price ||
                  firstPrice?.dining_price ||
                  firstPrice?.home_delivery_price ||
                  "",
                description: prev.isCustomDescription
                  ? prev.description
                  : getDefaultDescription(item),
              }));

              setItemSearch("");
              setShowItemDropdown(false);
            }}
            className="w-full text-left px-3 py-2 hover:bg-emerald-50"
          >
            {item.item_name}
          </button>
        ))}
      </div>
    </div>
  )}
</div>
              </div>

              <div>
                <label className={labelClass}>MFG Date</label>

                <input
                  type="date"
                  name="mfgDate"
                  value={formData.mfgDate}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>EXP Date</label>

                <input
                  type="date"
                  name="expDate"
                  value={formData.expDate}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>MRP</label>

                <input
                  type="number"
                  name="mrp"
                  value={formData.mrp}
                  onChange={handleChange}
                  placeholder="Example: 1000"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Label Quantity</label>

                <input
                  type="number"
                  name="labelQuantity"
                  value={formData.labelQuantity}
                  onChange={handleChange}
                  min="1"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Weight</label>

                <input
                  type="text"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  placeholder="Example: 6kg"
                  className={inputClass}
                />
              </div>

           <div>
  <label className={labelClass}>Select Type</label>

  <select
    name="designType"
    value={formData.designType}
    onChange={handleChange}
    className={inputClass}
  >
    <option value="">Select Type</option>
   <option value="1">Type 1 - 50 x 50</option>
<option value="2">Type 2 - 80 x 40</option>
<option value="3">Type 3 - 50 x 50</option>
  </select>
</div>

              <div className="md:col-span-2">
                <div className="mb-1.5 flex items-center justify-between">
  <label className="block text-sm font-medium text-slate-700">
    Description
  </label>

  {selectedItem && (
    <button
      type="button"
      onClick={() => {
        setFormData((prev) => ({
          ...prev,
          description: getDefaultDescription(selectedItem),
          isCustomDescription: false,
        }));
      }}
      className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
    >
      Use default
    </button>
  )}
</div>

                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                 placeholder="Default description will appear after item selection. You can edit it."
                  className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500">
                    Characters:{" "}
                    <span className="font-semibold text-slate-700">
                      {formData.description.length}
                    </span>
                  </span>

        <span
  className={`rounded-full px-3 py-1 font-semibold ${
    designType === "1"
      ? "bg-pink-50 text-pink-600"
      : designType === "2"
        ? "bg-blue-50 text-blue-600"
        : designType === "3"
          ? "bg-purple-50 text-purple-600"
          : "bg-slate-100 text-slate-500"
  }`}
>
  Payload type: {designType || "-"}
</span>

                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 md:flex-row md:items-center md:justify-between">
              <div className="text-xs text-slate-500">
                Weight payload format:{" "}
                <span className="font-semibold text-slate-700">
                  weight|qty|price|description|type
                </span>
              </div>

              <button
                type="submit"
                className="rounded-xl bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600 hover:shadow-md"
              >
                Create Label
              </button>
            </div>
          </form>
        </div>

{/* RIGHT PREVIEW */}

<div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
  <div className="flex min-h-[520px] items-center justify-center rounded-2xl bg-white p-6">
    {barcodeImage ? (
      <div className="flex items-center justify-center rounded-2xl bg-white p-4">
        <img
          src={barcodeImage}
          alt={`Barcode type ${designType}`}
         className={`block object-contain bg-white ${
  designType === "1" || designType === "3"
    ? "w-[320px] h-[320px]"
    : "w-[420px] h-[210px]"
}`}
          style={{
            imageRendering: "auto",
          }}
        />
      </div>
    ) : (
      <div className="flex h-[300px] w-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-sm font-medium text-slate-400">
        Enter description to preview barcode image
      </div>
    )}
  </div>
</div>
      </div>
      </div>
    </div>
    </>
  );
};

export default LabelPrintingSystem;
